import { compact } from 'lodash-es';
import { getLastCertificationReservationConditions } from '@lp/features/AssignmentTables/utils';
import { SuggestedSettingsEnum } from '@lp/features/RoutePlanner/types';
import { CandidateMatchType, CSAssignmentCandidate, RSAssignmentCandidate } from '@/api/apis/resources';
import { formatWarningMessage, WarningResourceType } from './utils';
import { Uuid } from '@/api/types';
import { TrialProgram } from '@/api/apis/facilities/trial-programs';
import { ResourceWarning, WarningType } from '../types';

type EmployeeCandidate = CSAssignmentCandidate | RSAssignmentCandidate;

type CSWithTrialIds = CSAssignmentCandidate & { trialProgramIds: Uuid[] };
const hasTrialIds = (cs: CSAssignmentCandidate): cs is CSWithTrialIds => 'trialProgramIds' in cs;

const hasTrialProgramCertification = (cs: CSAssignmentCandidate, trialProgram?: TrialProgram | null): boolean => {
  if (!trialProgram) return true;

  const byOrganOk = !!trialProgram.organ && (cs.employeeTrialCertificates ?? []).includes(trialProgram.organ);

  const byIdOk =
    hasTrialIds(cs) && Array.isArray(cs.trialProgramIds) ? cs.trialProgramIds.includes(trialProgram.id) : false;

  return byOrganOk || byIdOk;
};

const formatTrialProgramWarning = (csName: string, program: TrialProgram): string =>
  `Clinical Specialist ${csName}: No certification in the ${program.name} trial program. Make sure to select Clinical Specialist with appropriate certification.`;

type MatchWarningsConfig = {
  orTime: string;
  goTime: string;
  applyGoTime: boolean;
  trialProgram?: TrialProgram | null;
  toggleWarning: (warning: ResourceWarning) => void;
};

const getMediumMatchMessage = (
  employee: EmployeeCandidate,
  employeeType: WarningResourceType,
  config: MatchWarningsConfig,
): string => {
  const { orTime, goTime, applyGoTime } = config;
  const {
    employeeFullName,
    match,
    onCallBeforeTargetMinutes,
    onCallAfterTargetMinutes,
    onRestStartsAfterTargetMinutes,
    onRestEndsBeforeTargetMinutes,
  } = employee;

  if (match.matchType !== CandidateMatchType.Medium) {
    return '';
  }

  const rsUnavailabilities =
    employeeType === SuggestedSettingsEnum.RS ? (employee as RSAssignmentCandidate).unavailabilities : undefined;
  const showGoTime = applyGoTime && employeeType === SuggestedSettingsEnum.CS;

  return formatWarningMessage(
    showGoTime ? goTime : orTime,
    employeeType,
    employeeFullName,
    match,
    onCallBeforeTargetMinutes,
    onCallAfterTargetMinutes,
    onRestStartsAfterTargetMinutes,
    onRestEndsBeforeTargetMinutes,
    rsUnavailabilities,
    showGoTime,
  );
};

const getCertificationMessage = (employee: CSAssignmentCandidate, employeeType: WarningResourceType): string => {
  if (employeeType !== SuggestedSettingsEnum.CS || !employee.lastCertificationAtHubInfo) {
    return '';
  }

  const extra = getLastCertificationReservationConditions({
    lastCertificationAtHubInfo: employee.lastCertificationAtHubInfo,
    matchType: employee.match.matchType,
    roleLabel: 'Clinical Specialist',
    csName: employee.employeeFullName,
    withMayBecomeLast: true,
  });

  return extra.join(' ');
};

const addMediumMatchWarning = (
  employee: EmployeeCandidate,
  employeeType: WarningResourceType,
  config: MatchWarningsConfig,
): void => {
  const mediumMatchMessage = getMediumMatchMessage(employee, employeeType, config);
  const certificationMessage = getCertificationMessage(employee as CSAssignmentCandidate, employeeType);
  const message = compact([mediumMatchMessage, certificationMessage]).join(' ');

  if (message) {
    config.toggleWarning({
      id: `${employee.employeeId}:${employeeType}:medium-match`,
      type: WarningType.Match,
      employeeId: employee.employeeId,
      message,
    });
  }
};

const addTrialProgramWarning = (
  employee: EmployeeCandidate,
  employeeType: WarningResourceType,
  config: MatchWarningsConfig,
): void => {
  const { trialProgram, toggleWarning } = config;

  if (employeeType !== SuggestedSettingsEnum.CS || !trialProgram) {
    return;
  }

  const cs = employee as CSAssignmentCandidate;
  if (!hasTrialProgramCertification(cs, trialProgram)) {
    toggleWarning({
      id: `${employee.employeeId}:${employeeType}:trial-cert`,
      type: WarningType.Match,
      employeeId: employee.employeeId,
      message: formatTrialProgramWarning(employee.employeeFullName, trialProgram),
    });
  }
};

const processMatchWarning = (
  employee: EmployeeCandidate,
  employeeType: WarningResourceType,
  config: MatchWarningsConfig,
) => {
  addMediumMatchWarning(employee, employeeType, config);
  addTrialProgramWarning(employee, employeeType, config);
};

export const createMatchWarningsHandler = (config: MatchWarningsConfig) => {
  return (employee: EmployeeCandidate, employeeType: WarningResourceType) => {
    processMatchWarning(employee, employeeType, config);
  };
};
